import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Zap, Users, Lock, TrendingUp, HeadphonesIcon } from 'lucide-react';
import { whyChooseUs } from '../data/mockData';

const WhyUs = () => {
  const IconMap = {
    'Expert Scientific Team': Users,
    'Rapid Development': Zap,
    'Deep Customization': Award,
    'IP Confidentiality': Lock,
    'Quality Assurance': Award,
    'End-to-End Support': HeadphonesIcon
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-secondary">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <div className="inline-block mb-6">
              <span className="text-sm font-semibold text-brand-teal uppercase tracking-wider">
                Our Advantage
              </span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-light leading-tight mb-6">
              Why Leading Brands <span className="font-medium text-brand-teal">Partner With Us</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              A unique combination of scientific expertise, speed, and partnership approach that 
              sets us apart in the cosmetic R&D landscape.
            </p>
          </div>
        </div>
      </section>

      {/* Key Differentiators */}
      <section className="py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => {
              const IconComponent = IconMap[item.title] || Award;
              return (
                <div
                  key={index}
                  className="bg-background border border-border p-8 hover-lift group"
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-12 h-12 bg-brand-teal/10 flex items-center justify-center group-hover:bg-brand-teal transition-colors duration-200">
                      <IconComponent className="text-brand-teal group-hover:text-white transition-colors duration-200" size={24} />
                    </div>
                    <span className="text-2xl font-semibold text-brand-teal">{item.metric}</span>
                  </div>
                  <h3 className="text-xl font-medium mb-3">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Competitive Advantages */}
      <section className="py-24 bg-secondary">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light mb-4">
              What Makes Us <span className="font-medium text-brand-teal">Different</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Beyond technical capabilities, it's our approach to partnership that drives success
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  title: "Founder-Friendly Approach",
                  description: "We understand startup constraints. Flexible MOQs, staged payments, and consultation support designed for emerging brands."
                },
                {
                  title: "Transparent Communication",
                  description: "Regular updates, open discussion of challenges, and honest timelines. No surprises, just clear collaboration."
                },
                {
                  title: "Latest Technology Access",
                  description: "Investment in cutting-edge testing equipment and formulation technologies keeps you ahead of market trends."
                },
                {
                  title: "Market Intelligence",
                  description: "Insights into emerging ingredients, consumer trends, and competitive landscape inform better product decisions."
                },
                {
                  title: "Regulatory Expertise",
                  description: "Navigate complex compliance requirements with confidence. Our team stays current with evolving regulations."
                },
                {
                  title: "Long-Term Partnership",
                  description: "We grow with your brand. From first product to full portfolio expansion, we're invested in your success."
                }
              ].map((advantage, index) => (
                <div key={index} className="bg-background p-8">
                  <h3 className="text-xl font-medium mb-3">{advantage.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {advantage.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Certifications & Compliance */}
      <section className="py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light mb-4">
              Certifications & <span className="font-medium text-brand-teal">Standards</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Meeting and exceeding industry quality and safety benchmarks
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                "ISO 9001:2015",
                "ISO 22716 (GMP)",
                "CDSCO Approved",
                "FDA Compliant"
              ].map((cert, index) => (
                <div
                  key={index}
                  className="aspect-square border-2 border-border flex items-center justify-center p-6 hover-lift"
                >
                  <span className="text-center font-medium text-foreground">{cert}</span>
                </div>
              ))}
            </div>

            <div className="mt-12 p-8 bg-secondary text-center">
              <p className="text-sm text-muted-foreground">
                All formulations developed under strict quality management systems with comprehensive 
                documentation and traceability protocols.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Success Metrics */}
      <section className="py-24 bg-foreground text-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light mb-4">
              Proven Track Record
            </h2>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Numbers that demonstrate our commitment to excellence and client success
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {[
              { metric: "500+", label: "Formulations Developed" },
              { metric: "150+", label: "Active Brand Partners" },
              { metric: "98%", label: "Client Satisfaction" },
              { metric: "40%", label: "Faster Time-to-Market" }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl lg:text-5xl font-light mb-2 text-brand-teal">{stat.metric}</p>
                <p className="text-sm text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto bg-secondary p-12 lg:p-16">
            <div className="text-center">
              <h2 className="text-3xl lg:text-4xl font-light mb-6">
                Experience the Difference
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed max-w-2xl mx-auto">
                Join innovative brands who've transformed their product vision into market success with our partnership.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-sm font-semibold text-white bg-brand-teal hover:opacity-90"
              >
                Start Your Partnership
                <ArrowRight size={18} className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyUs;
